import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { 
  subscribeToUnreadMessages, 
  markConversationAsRead,
  getUnreadMessagesCount
} from '../services/messageService';

export const MessageContext = createContext();

export const useMessages = () => useContext(MessageContext);

export const MessageProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Initial fetch of unread messages count
  useEffect(() => {
    const fetchInitialCount = async () => {
      if (!currentUser) {
        setUnreadCount(0);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Check if the user is a partner based on role
        const isPartner = currentUser.role === 'partner';
        
        // Get initial count
        const count = await getUnreadMessagesCount(currentUser.uid, isPartner);
        setUnreadCount(count);
      } catch (error) {
        console.error('Error fetching initial unread count:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialCount();
  }, [currentUser]);

  // Set up subscription to unread messages
  useEffect(() => {
    if (!currentUser) {
      setUnreadCount(0);
      return;
    }

    // Check if the user is a partner based on role
    const isPartner = currentUser.role === 'partner';
    
    console.log('Setting up message subscription for', currentUser.uid, 'isPartner:', isPartner);
    
    // Subscribe to unread messages
    const unsubscribe = subscribeToUnreadMessages(
      currentUser.uid,
      isPartner,
      (count) => {
        console.log('Unread message count update:', count);
        setUnreadCount(count);
      }
    );
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // Mark conversation as read
  const markAsRead = async (conversationId) => {
    if (!currentUser || !conversationId) return;
    
    try {
      await markConversationAsRead(conversationId, currentUser.uid);
    } catch (err) {
      console.error('Error marking conversation as read:', err);
    }
  };

  const value = {
    unreadCount,
    loading,
    markAsRead
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
    </MessageContext.Provider>
  );
}; 